enum SkillRating {
  Unknown = 0,
  WillingToLearn,
  Student,
  Proficient,
  Teacher,
  Leader,
  MovedOn = 99,
}

export default SkillRating;
export { SkillRating };

type SkillRatingPresentation = {
  description: string;
  colour: string;
  fontColour: string;
};

const presentationValues = new Map<SkillRating, SkillRatingPresentation>([
  [
    SkillRating.Unknown,
    { description: "Unknown", colour: "#cccccc", fontColour: "black" },
  ],
  [
    SkillRating.MovedOn,
    { description: "Moved On", colour: "#9e9e9e", fontColour: "black" },
  ],
  [
    SkillRating.WillingToLearn,
    { description: "Willing to Learn", colour: "#ebafbe", fontColour: "black" },
  ],
  [
    SkillRating.Student,
    { description: "Student", colour: "#d782a0", fontColour: "black" },
  ],
  [
    SkillRating.Proficient,
    { description: "Proficient", colour: "#6baed6", fontColour: "white" },
  ],
  [
    SkillRating.Teacher,
    { description: "Teacher", colour: "#08519c", fontColour: "white" },
  ],
  [
    SkillRating.Leader,
    { description: "Leader", colour: "#7828a0", fontColour: "white" },
  ],
]);

const fallbackPresentation: SkillRatingPresentation = {
  description: "Unknown",
  colour: "#cccccc",
  fontColour: "black",
};

function getPresentation(skillRating: SkillRating): SkillRatingPresentation {
  return presentationValues.get(skillRating) ?? fallbackPresentation;
}

export function skillRatingToString(skillRating: SkillRating): string {
  return getPresentation(skillRating).description;
}

export function getSkillRatingColour(skillRating: SkillRating): string {
  return getPresentation(skillRating).colour;
}

export function getSkillRatingTextColour(skillRating: SkillRating): string {
  return getPresentation(skillRating).fontColour;
}

export function skillRatingStringToValue(
  value: string,
): SkillRating | undefined {
  const result = Array.from(presentationValues.entries()).find(
    ([, v]) => v.description === value,
  );
  return result ? result[0] : undefined;
}

export function skillRatingKeys(): SkillRating[] {
  return Array.from(presentationValues.keys());
}

export function skillRatingValues(): SkillRatingPresentation[] {
  return Array.from(presentationValues.values());
}

/**
 * Chart order (visual ordering), not used for proficiency comparisons.
 */
export function getSkillChartOrder(): SkillRating[] {
  return [
    SkillRating.Leader,
    SkillRating.Teacher,
    SkillRating.Proficient,
    SkillRating.Student,
    SkillRating.WillingToLearn,
    SkillRating.MovedOn,
    SkillRating.Unknown,
  ];
}

/**
 * Proficiency index (for comparisons / ranges / weighting).
 * - Unknown: 0
 * - WillingToLearn: 1
 * - Student: 2
 * - Proficient: 3
 * - Teacher: 4
 * - Leader: 5
 * - MovedOn: 99 (special case)
 */
export function getSkillRatingIndex(skillRating: SkillRating | number): number {
  // Accept raw numeric payloads (API often returns 0..5 or 99)
  if (typeof skillRating === "number" && Number.isFinite(skillRating)) {
    if (skillRating === 99) return 99;
    if (skillRating >= 0 && skillRating <= 5) return skillRating;
  }

  switch (skillRating as SkillRating) {
    case SkillRating.Unknown:
      return 0;
    case SkillRating.WillingToLearn:
      return 1;
    case SkillRating.Student:
      return 2;
    case SkillRating.Proficient:
      return 3;
    case SkillRating.Teacher:
      return 4;
    case SkillRating.Leader:
      return 5;
    case SkillRating.MovedOn:
      return 99;
    default:
      return 0;
  }
}
